// src/components/Seguridad/Components/Tabs/Empleados/EmpleadosZonasGeograficasTab.jsx
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Seguridad/Utils/Endpoints';
import { Models } from '../../../Seguridad/Utils/ModelsSeguridad';
import AlertDialog from '../../../Lms/Components/Shared/AlertDialog';

const TITLETAB = 'Asignación de Zonas Geográficas';

// Endpoints
const endpointAssigned = ENDPOINTS.empleadosZonasGeograficas;
const endpointAssignedById = ENDPOINTS.empleadosZonasGeograficasById;
const endpointAvailable = ENDPOINTS.zonasGeograficas;

// Models
const normalizeAssigned = Models.empleadosZonasGeograficas;
const normalizeAvailable = Models.zonasGeograficas;

// Keys
const ID_ASSIGNED_KEY = 'idEmpleadoZona';
const ID_ITEM_KEY = 'idZonaGeografica';
const FK_USER_KEY = 'idEmpleado';
const ITEM_NAME_KEY = 'nombre';

export default function EmpleadosZonasGeograficasTab({ fkValue, onClose, onDirtyChange }) {
  const [loading, setLoading] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });

  // State for initial data (to calculate changes)
  const [initialAssigned, setInitialAssigned] = useState([]);

  // State for current local changes
  const [currentAssigned, setCurrentAssigned] = useState([]);

  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [resAvailable, resAssigned] = await Promise.all([
        axios.get(endpointAvailable),
        axios.get(endpointAssigned),
      ]);

      const items = (resAvailable?.data || []).map(normalizeAvailable);
      const rawAssigned = (resAssigned?.data || []).map(normalizeAssigned);

      // Filter for current employee
      const myAssigned = rawAssigned.filter(r => Number(r[FK_USER_KEY]) === Number(fkValue));

      setAllItems(items);
      setInitialAssigned(myAssigned);
      setCurrentAssigned(myAssigned);
    } catch (e) {
      console.error(e);
      setAlertDialog({ open: true, title: 'Error', message: `Error al cargar ${TITLETAB}`, variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fkValue) {
      load();
    }
  }, [fkValue]);

  // Calculate available items (All Items - Current Assigned Items)
  const availableItems = useMemo(() => {
    const assignedIds = new Set(currentAssigned.map(r => r[ID_ITEM_KEY]));
    return allItems.filter(r => !assignedIds.has(r[ID_ITEM_KEY]));
  }, [allItems, currentAssigned]);

  // Enriched assigned items (add name from catalog)
  const enrichedAssigned = useMemo(() => {
    return currentAssigned.map(assigned => {
      const itemDef = allItems.find(i => i[ID_ITEM_KEY] === assigned[ID_ITEM_KEY]);
      return {
        ...assigned,
        _displayName: itemDef ? (itemDef[ITEM_NAME_KEY] || itemDef.nombre) : `Item #${assigned[ID_ITEM_KEY]}`,
      };
    });
  }, [currentAssigned, allItems]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (initialAssigned.length !== currentAssigned.length) return true;

    const initialIds = new Set(initialAssigned.map(r => r[ID_ITEM_KEY]));
    const currentIds = new Set(currentAssigned.map(r => r[ID_ITEM_KEY]));

    for (let id of currentIds) {
      if (!initialIds.has(id)) return true;
    }
    return false;
  }, [initialAssigned, currentAssigned]);

  // Propagar estado "sucio"
  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(hasChanges);
    }
  }, [hasChanges, onDirtyChange]);

  // --- Drag and Drop Handlers ---

  const handleDragStart = (e, item, source) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetColumn) => {
    e.preventDefault();
    if (dragOverColumn !== targetColumn) {
      setDragOverColumn(targetColumn);
    }
  };

  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    setDragOverColumn(null);

    const dataStr = e.dataTransfer.getData('application/json');
    if (!dataStr) return;

    const { item, source } = JSON.parse(dataStr);

    if (source === targetColumn) return;

    if (targetColumn === 'assigned' && source === 'available') {
      addLocal(item);
    } else if (targetColumn === 'available' && source === 'assigned') {
      removeLocal(item);
    }
  };

  // --- Local State Modifiers ---

  const addLocal = (item) => {
    const exists = currentAssigned.some(r => r[ID_ITEM_KEY] === item[ID_ITEM_KEY]);
    if (exists) return;

    const newAssignment = {
      [ID_ASSIGNED_KEY]: 0, // Temporary ID
      [FK_USER_KEY]: Number(fkValue),
      [ID_ITEM_KEY]: item[ID_ITEM_KEY],
    };

    setCurrentAssigned(prev => [...prev, newAssignment]);
  };

  const removeLocal = (assignedItem) => {
    setCurrentAssigned(prev => prev.filter(r => r[ID_ITEM_KEY] !== assignedItem[ID_ITEM_KEY]));
  };

  // --- Save / Cancel ---

  const handleCancel = () => {
    onClose?.();
    setCurrentAssigned(initialAssigned);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // 1. Identify to ADD
      const initialIds = new Set(initialAssigned.map(r => r[ID_ITEM_KEY]));
      const toAdd = currentAssigned.filter(r => !initialIds.has(r[ID_ITEM_KEY]));

      // 2. Identify to REMOVE
      const currentIds = new Set(currentAssigned.map(r => r[ID_ITEM_KEY]));
      const toRemove = initialAssigned.filter(r => !currentIds.has(r[ID_ITEM_KEY]));

      // 3. Execute requests
      const promises = [];

      // Additions
      for (const item of toAdd) {
        const payload = {
          [ID_ASSIGNED_KEY]: 0,
          [FK_USER_KEY]: Number(fkValue),
          [ID_ITEM_KEY]: item[ID_ITEM_KEY],
        };
        promises.push(axios.post(endpointAssigned, payload));
      }

      // Removals
      for (const item of toRemove) {
        const idToDelete = item[ID_ASSIGNED_KEY];
        if (idToDelete > 0) {
          const url = typeof endpointAssignedById === 'function'
            ? endpointAssignedById(idToDelete)
            : `${endpointAssignedById}/${idToDelete}`;
          promises.push(axios.delete(url));
        }
      }

      await Promise.all(promises);

      await load();
      await load();
      setAlertDialog({ open: true, title: 'Éxito', message: 'Cambios guardados correctamente.', variant: 'success' });

    } catch (e) {
      console.error(e);
      setAlertDialog({ open: true, title: 'Error', message: 'Error al guardar los cambios.', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mini-crud">
      <div className="mini-topbar">
        <div>
          <h3 className="roles-title" style={{ margin: 0 }}>{TITLETAB}</h3>
          <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
            Arrastra o usa los botones (+ / x) para modificar. Guarda al finalizar.
          </div>
        </div>
      </div>

      <div className="dnd-container">
        {/* Left: Available */}
        <div
          className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
          onDragOver={(e) => handleDragOver(e, 'available')}
          onDrop={(e) => handleDrop(e, 'available')}
        >
          <div className="dnd-header">Disponibles (Origen)</div>
          {loading && availableItems.length === 0 ? (
            <div className="dnd-empty">Cargando...</div>
          ) : availableItems.length === 0 ? (
            <div className="dnd-empty">No hay elementos disponibles</div>
          ) : (
            availableItems.map(item => (
              <div
                key={item[ID_ITEM_KEY]}
                className="dnd-item"
                draggable={!saving}
                onDragStart={(e) => handleDragStart(e, item, 'available')}
              >
                <span>{item[ITEM_NAME_KEY] || item.nombre || `Item #${item[ID_ITEM_KEY]}`}</span>
                <button
                  type="button"
                  onClick={() => addLocal(item)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.2em',
                    color: '#28a745',
                    cursor: 'pointer',
                    padding: '0 5px'
                  }}
                  title="Agregar"
                >
                  ＋
                </button>
              </div>
            ))
          )}
        </div>

        {/* Right: Assigned */}
        <div
          className={`dnd-column ${dragOverColumn === 'assigned' ? 'drag-over' : ''}`}
          onDragOver={(e) => handleDragOver(e, 'assigned')}
          onDrop={(e) => handleDrop(e, 'assigned')}
        >
          <div className="dnd-header">Asignados (Destino)</div>
          {loading && enrichedAssigned.length === 0 ? (
            <div className="dnd-empty">Cargando...</div>
          ) : enrichedAssigned.length === 0 ? (
            <div className="dnd-empty">Arrastra aquí para asignar</div>
          ) : (
            enrichedAssigned.map(item => (
              <div
                key={item[ID_ITEM_KEY]}
                className="dnd-item"
                draggable={!saving}
                onDragStart={(e) => handleDragStart(e, item, 'assigned')}
              >
                <span>{item._displayName}</span>
                <button
                  type="button"
                  onClick={() => removeLocal(item)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.2em',
                    color: '#dc3545',
                    cursor: 'pointer',
                    padding: '0 5px'
                  }}
                  title="Remover"
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1rem' }}>
        <button
          className="roles-btn"
          onClick={handleCancel}
          type="button"
        >
          Cancelar / Reestablecer
        </button>
        <button
          className="btn-action"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          style={{
            opacity: (!hasChanges || saving) ? 0.5 : 1,
            cursor: (!hasChanges || saving) ? 'not-allowed' : 'pointer'
          }}
          type="button"
        >
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </button>
      </div>

      {alertDialog.open && (
        <AlertDialog
          open={alertDialog.open}
          onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
          title={alertDialog.title}
          message={alertDialog.message || ""}
          variant={alertDialog.variant || "info"}
        />
      )}
    </div>
  );
}
